import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Isvg from 'react-inlinesvg';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import Button from 'components/Button';
import Message from 'components/Message';
import { closeModal } from 'components/Modal/Modal.reducers';
import { PlainSwitch } from 'components/Switch';
import { ICONS } from 'constants/icons';
import { bem } from 'helpers/class-name';
import { reportError } from 'helpers/error-logging';

import {
  buildBusinessHoursPayload,
  buildHoursStateFromPayload,
  createDefaultRange,
  createInitialHoursState,
  DAY_OPTIONS,
  getHoursValidationError,
} from './AddMenuFlyout.helpers';
import messages from './AddMenuFlyout.messages';
import AvailabilityToggle from './AvailabilityToggle';
import IntegratedPartnerBanner from './IntegratedPartnerBanner';
import TimeDropdown from './TimeDropdown';

import './AddMenuFlyout.scss';

const { block, element } = bem('add-menu-modal');
export const ADD_MENU_MODAL_NAME = 'AddMenuModal';
export const EDIT_MENU_MODAL_NAME = 'EditMenuModal';

const ERROR_TYPE_NAME = 'name';
const ERROR_TYPE_HOURS = 'hours';

const DayHoursSection = ({
  day,
  dayState,
  isSubmitting,
  dayHasError,
  hoursError,
  formError,
  onToggleDay,
  onChangeRange,
  onAddRange,
  onRemoveRange,
  disabled,
}) => {
  const intl = useIntl();
  const dayLabel = intl.formatMessage(day.labelMessage);
  const isDisabled = disabled || isSubmitting || !dayState.enabled;

  return (
    <section className={element('day')}>
      <div className={element('day-header')}>
        <PlainSwitch
          className={element('day-switch')}
          checked={dayState.enabled}
          name={`add-menu-${day.key.toLowerCase()}`}
          disabled={disabled || isSubmitting}
          inputProps={{
            'aria-label': dayLabel,
            onChange: () => onToggleDay(day.key),
          }}
        />
        <span className={element('day-label')}>{dayLabel}</span>
      </div>
      <div className={element('ranges-wrapper')}>
        <div className={element('ranges')}>
          {dayState.ranges.map((range, index) => {
            const isRangeError = dayHasError && hoursError?.highlight === 'range' && hoursError?.rangeIndex === index;

            return (
              <div className={element('range-row')} key={range.id}>
                <TimeDropdown
                  className={element('time-dropdown', { error: isRangeError })}
                  disabled={isDisabled}
                  label={`${dayLabel.toLowerCase()}-start-${index + 1}`}
                  prefixLabel={intl.formatMessage(messages.startLabel)}
                  value={range.start}
                  onChange={nextValue => onChangeRange(day.key, index, 'start', nextValue)}
                />
                <TimeDropdown
                  className={element('time-dropdown', { error: isRangeError })}
                  disabled={isDisabled}
                  label={`${dayLabel.toLowerCase()}-end-${index + 1}`}
                  prefixLabel={intl.formatMessage(messages.endLabel)}
                  value={range.end}
                  onChange={nextValue => onChangeRange(day.key, index, 'end', nextValue)}
                />
                {dayState.ranges.length === 1 && (
                  <button
                    type="button"
                    className={element('range-icon-button', { muted: !dayState.enabled })}
                    onClick={() => onAddRange(day.key)}
                    disabled={isDisabled}
                    aria-label={intl.formatMessage(messages.addTimeRangeAriaLabel, {
                      dayLabel,
                    })}>
                    <Isvg src={ICONS.addBlue} />
                  </button>
                )}
                {dayState.ranges.length > 1 && (
                  <button
                    type="button"
                    className={element('range-icon-button', 'remove')}
                    onClick={() => onRemoveRange(day.key, index)}
                    disabled={disabled || isSubmitting}
                    aria-label={intl.formatMessage(messages.removeTimeRangeAriaLabel, {
                      dayLabel,
                      index: index + 1,
                    })}>
                    <Isvg src={ICONS.trashCanBlack} />
                  </button>
                )}
              </div>
            );
          })}
          {dayState.ranges.length > 1 && (
            <button
              type="button"
              className={element('add-times-button', { muted: !dayState.enabled })}
              onClick={() => onAddRange(day.key)}
              disabled={isDisabled}
              aria-label={intl.formatMessage(messages.addTimeRangeAriaLabel, {
                dayLabel,
              })}>
              <Isvg src={ICONS.addBlue} className={element('add-times-icon')} />
              <Message message={messages.addTimes} />
            </button>
          )}
        </div>
        {dayHasError && <p className={element('field-error')}>{formError?.message}</p>}
      </div>
    </section>
  );
};

DayHoursSection.propTypes = {
  day: PropTypes.shape({
    key: PropTypes.string.isRequired,
    labelMessage: PropTypes.shape({
      id: PropTypes.string,
      defaultMessage: PropTypes.string,
    }).isRequired,
  }).isRequired,
  dayState: PropTypes.shape({
    enabled: PropTypes.bool.isRequired,
    ranges: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        start: PropTypes.string,
        end: PropTypes.string,
      })
    ).isRequired,
  }).isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  dayHasError: PropTypes.bool.isRequired,
  hoursError: PropTypes.shape({
    highlight: PropTypes.string,
    rangeIndex: PropTypes.number,
  }),
  formError: PropTypes.shape({
    message: PropTypes.string,
  }),
  onToggleDay: PropTypes.func.isRequired,
  onChangeRange: PropTypes.func.isRequired,
  onAddRange: PropTypes.func.isRequired,
  onRemoveRange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

DayHoursSection.defaultProps = {
  hoursError: null,
  formError: null,
  disabled: false,
};

const AddMenuModal = ({ onSubmit, onClose, initialData, isReadOnly, integrationName }) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const isEditMode = Boolean(initialData);
  const resolvedModalName = isEditMode ? EDIT_MENU_MODAL_NAME : ADD_MENU_MODAL_NAME;

  const [name, setName] = useState(initialData?.name || '');
  const [hoursState, setHoursState] = useState(() =>
    isEditMode ? buildHoursStateFromPayload(initialData?.businessHours) : createInitialHoursState()
  );
  const [enabled, setEnabled] = useState(initialData?.enabled ?? true);
  const [formError, setFormError] = useState(null);
  const [hoursError, setHoursError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const dayErrorKey = hoursError?.dayKey || null;
  const hasNameError = formError?.type === ERROR_TYPE_NAME;
  const hasGenericHoursError = formError?.type === ERROR_TYPE_HOURS && !dayErrorKey;

  const resetState = () => {
    setName(initialData?.name || '');
    setHoursState(isEditMode ? buildHoursStateFromPayload(initialData?.businessHours) : createInitialHoursState());
    setEnabled(initialData?.enabled ?? true);
    setFormError(null);
    setHoursError(null);
  };

  const handleClose = () => {
    if (isSubmitting) {
      return;
    }
    resetState();
    onClose?.();
    dispatch(closeModal(resolvedModalName));
  };

  const handleToggleDay = dayKey => {
    setFormError(null);
    setHoursError(null);
    setHoursState(previous => ({
      ...previous,
      [dayKey]: {
        ...previous[dayKey],
        enabled: !previous[dayKey].enabled,
      },
    }));
  };

  const handleChangeRange = (dayKey, index, field, value) => {
    setFormError(null);
    setHoursError(null);
    setHoursState(previous => ({
      ...previous,
      [dayKey]: {
        ...previous[dayKey],
        ranges: previous[dayKey].ranges.map((range, rangeIndex) =>
          rangeIndex === index
            ? {
                ...range,
                [field]: value,
              }
            : range
        ),
      },
    }));
  };

  const handleAddRange = dayKey => {
    setFormError(null);
    setHoursError(null);
    setHoursState(previous => ({
      ...previous,
      [dayKey]: {
        ...previous[dayKey],
        ranges: [...previous[dayKey].ranges, createDefaultRange()],
      },
    }));
  };

  const handleRemoveRange = (dayKey, index) => {
    setFormError(null);
    setHoursError(null);
    setHoursState(previous => ({
      ...previous,
      [dayKey]: {
        ...previous[dayKey],
        ranges: previous[dayKey].ranges.filter((_, rangeIndex) => rangeIndex !== index),
      },
    }));
  };

  const handleSubmit = async event => {
    event.preventDefault();
    if (!onSubmit) return;
    const trimmedName = name.trim();
    if (!trimmedName) {
      setFormError({ type: ERROR_TYPE_NAME, message: intl.formatMessage(messages.nameRequired) });
      setHoursError(null);
      return;
    }

    const hoursValidationError = getHoursValidationError(hoursState);
    if (hoursValidationError) {
      setFormError({
        type: ERROR_TYPE_HOURS,
        message: intl.formatMessage(hoursValidationError.messageDescriptor),
      });
      setHoursError(hoursValidationError);
      return;
    }

    setFormError(null);
    setHoursError(null);
    const businessHours = buildBusinessHoursPayload(hoursState);
    setIsSubmitting(true);

    try {
      const payload = { name: trimmedName, businessHours };
      if (isEditMode) {
        payload.enabled = enabled;
      }
      await onSubmit(payload);
      handleClose();
    } catch (error) {
      // error snackbar is dispatched by the submit handler in SkuMenuPage
      reportError('AddMenuModal submit failed', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={block()}>
      <header className={element('header')}>
        <h2 className={element('title')}>
          {isReadOnly && <Message message={messages.viewMenuTitle} values={{ menuName: initialData?.name || '' }} />}
          {!isReadOnly && isEditMode && (
            <Message message={messages.editMenuTitle} values={{ menuName: initialData?.name || '' }} />
          )}
          {!isReadOnly && !isEditMode && <Message message={messages.heading} />}
        </h2>
        <button
          type="button"
          className={element('close-button')}
          onClick={handleClose}
          aria-label={intl.formatMessage(messages.closeAriaLabel)}>
          <Isvg src={ICONS.cross} className={element('close-icon')} />
        </button>
      </header>
      <form className={element('form')} onSubmit={isReadOnly ? e => e.preventDefault() : handleSubmit}>
        <div className={element('fields')}>
          {isReadOnly && <IntegratedPartnerBanner integrationName={integrationName} />}
          <div className={element('name-row', { 'with-availability': isEditMode })}>
            <div className={element('name-field')}>
              <div className={element('label-row')}>
                <label className={element('label')} htmlFor="add-menu-name">
                  <Message message={messages.menuNameLabel} />
                </label>
                {!isReadOnly && (
                  <span className={element('required')}>
                    <Message message={messages.required} />
                  </span>
                )}
              </div>
              <input
                id="add-menu-name"
                className={element('input', { error: hasNameError })}
                value={name}
                onChange={event => {
                  setName(event.target.value);
                  if (hasNameError) {
                    setFormError(null);
                  }
                }}
                placeholder={intl.formatMessage(messages.placeholder)}
                disabled={isReadOnly || isSubmitting}
              />
              {hasNameError && <p className={element('field-error')}>{formError.message}</p>}
            </div>
            {isEditMode && (
              <div className={element('availability-field')}>
                <AvailabilityToggle
                  enabled={enabled}
                  onChangeEnabled={setEnabled}
                  isReadOnly={isReadOnly}
                  testId="edit-menu-availability-toggle"
                />
              </div>
            )}
          </div>
          {hasGenericHoursError && <p className={element('field-error')}>{formError.message}</p>}
        </div>

        <div className={element('body')}>
          <div className={element('hours-list')}>
            {DAY_OPTIONS.map(day => (
              <DayHoursSection
                key={day.key}
                day={day}
                dayState={hoursState[day.key]}
                isSubmitting={isSubmitting}
                dayHasError={dayErrorKey === day.key}
                hoursError={hoursError}
                formError={formError}
                onToggleDay={handleToggleDay}
                onChangeRange={handleChangeRange}
                onAddRange={handleAddRange}
                onRemoveRange={handleRemoveRange}
                disabled={isReadOnly}
              />
            ))}
          </div>
        </div>
        <footer className={element('footer')}>
          {isReadOnly ? (
            <Button type="button" styled="tertiary" onClick={handleClose}>
              <Message message={messages.cancel} />
            </Button>
          ) : (
            <>
              <Button
                type="button"
                styled="secondary"
                className={element('cancel-button')}
                onClick={handleClose}
                disabled={isSubmitting}>
                <Message message={messages.cancel} />
              </Button>
              <Button type="submit" styled="primary" waiting={isSubmitting} disabled={!name.trim() || isSubmitting}>
                <Message message={isEditMode ? messages.saveButton : messages.submit} />
              </Button>
            </>
          )}
        </footer>
      </form>
    </div>
  );
};

AddMenuModal.propTypes = {
  initialData: PropTypes.shape({
    name: PropTypes.string,
    businessHours: PropTypes.arrayOf(
      PropTypes.shape({
        dayOfWeek: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      })
    ),
    enabled: PropTypes.bool,
  }),
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  isReadOnly: PropTypes.bool,
  integrationName: PropTypes.string,
};

AddMenuModal.defaultProps = {
  initialData: null,
  onClose: null,
  onSubmit: null,
  isReadOnly: false,
  integrationName: undefined,
};

AddMenuModal.modalConfig = {
  shouldCloseOnOverlayClick: true,
};

export { AddMenuModal as EditMenuModal };
export default AddMenuModal;
